import { useEffect, useState } from 'react';
import { ChevronRight, FileText, ListChecks, PlayCircle } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';

// How far back quality-service looks for testing activity
export const RECENT_DAYS = 14;

// "today", "yesterday", "3 days ago", by the viewer's calendar days, not 24-hour periods: 23:30 yesterday is
// "yesterday" at 00:30. Dates are compared at local midnight and rounded, so a daylight-saving day of 23 or
// 25 hours still counts as one. A missing or unreadable date gives ''
export function daysAgo(date, now = new Date()) {
  // new Date(null) is 1970, not "no date"
  if (date === null || date === undefined || date === '') return '';
  const then = new Date(date);
  if (Number.isNaN(then.getTime())) return '';
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(new Date(now)) - midnight(then)) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function Activity({ icon: Icon, children }) {
  return (
    <span className="str-recent-activity">
      <Icon size={13} aria-hidden="true" /> {children}
    </span>
  );
}

// Epics with testing activity in the last RECENT_DAYS (new test cases, a published STP, test runs),
// most recent first, from GET /hub/software-test-report/recent-epics. Clicking one opens its report.
// The list is a shortcut: when it can't load, it says so and the epic key field still works
function RecentEpics({ onSelect }) {
  const [epics, setEpics] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`${QUALITY_SERVICE_API_URL}/hub/software-test-report/recent-epics`, { params: { days: RECENT_DAYS } })
      .then((response) => {
        if (!cancelled) setEpics(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="str-recent" aria-labelledby="str-recent-title">
      <header className="str-recent-header">
        <h3 id="str-recent-title">Recently active epics</h3>
        <span>Testing activity in the last {RECENT_DAYS} days</span>
      </header>

      {failed ? (
        <p className="str-recent-note">Couldn't load the recent epics. Enter an epic key above instead.</p>
      ) : epics === null ? (
        <ul className="str-recent-list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="str-recent-skeleton" />
          ))}
        </ul>
      ) : epics.length === 0 ? (
        <p className="str-recent-note">No epic had testing activity in the last {RECENT_DAYS} days.</p>
      ) : (
        <ul className="str-recent-list">
          {epics.map((epic) => (
            <li key={epic.epicKey}>
              <button type="button" className="str-recent-item" onClick={() => onSelect(epic.epicKey)}>
                <span className="str-recent-main">
                  <span className="str-recent-title">
                    <span className="str-recent-key">{epic.epicKey}</span>
                    <span className="str-recent-summary">{epic.summary}</span>
                  </span>
                  <span className="str-recent-meta">
                    {[epic.unit, epic.status].filter(Boolean).join(' · ')}
                  </span>
                  <span className="str-recent-activities">
                    {epic.newTestCases > 0 && (
                      <Activity icon={ListChecks}>
                        {epic.newTestCases} new test case{epic.newTestCases === 1 ? '' : 's'}
                      </Activity>
                    )}
                    {daysAgo(epic.stpPublishedAt) && (
                      <Activity icon={FileText}>STP published {daysAgo(epic.stpPublishedAt)}</Activity>
                    )}
                    {epic.testRuns > 0 && (
                      <Activity icon={PlayCircle}>
                        {epic.testRuns} test run{epic.testRuns === 1 ? '' : 's'}
                      </Activity>
                    )}
                  </span>
                </span>
                <span className="str-recent-when">{daysAgo(epic.lastActivityAt)}</span>
                <ChevronRight size={18} className="str-recent-chevron" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default RecentEpics;
